import { CustomEvent, CustomEvents, Visibility } from '@nextforce/api'
import { Text, makeStyles, useTheme } from '@rneui/themed'
import { onlineManager } from '@tanstack/react-query'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import { useState } from 'react'
import { View } from 'react-native'
import { Card } from 'shared/components/card'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { useUser } from 'users/queries/detail'
import { EventDescriptionModal } from '../../../../shared/components/event_description_modal'
import { ReadMoreView } from '../../../../shared/components/read_more'
export const CUSTOM_EVENT_HEIGHT = 120

export const CUSTOM_EVENT_TOP_MARGIN = 5

interface CustomEventViewProps {
  event: CustomEvents
  onEditCustomEvent: (customEvent: CustomEvent) => void
}

export default function CustomEventView({
  event,
  onEditCustomEvent,
}: CustomEventViewProps) {
  if (!event.customEvents.length) {
    return null
  }

  return (
    <View style={{ flex: 1 }}>
      {getCustomEventViews(event.customEvents, onEditCustomEvent)}
    </View>
  )
}

function getCustomEventViews(
  events: CustomEvent[],
  onEditCustomEvent: (CustomEvent: CustomEvent) => void
) {
  return events.map((customEvent, index) => (
    <CustomEventCard
      customEvent={customEvent}
      key={String(index)}
      onEditCustomEvent={onEditCustomEvent}
    />
  ))
}

interface CustomEventCardProps {
  customEvent: CustomEvent
  onEditCustomEvent: (customEvent: CustomEvent) => void
}

function CustomEventCard({
  customEvent,
  onEditCustomEvent,
}: CustomEventCardProps) {
  const theme = useTheme()
  const styles = useStyles(theme)
  const { data: user } = useUser()
  const canEditCustomEvent = user?.permissions?.canEditCustomEvents
  const [modalVisible, setModalVisible] = useState(false)

  return (
    <Card style={styles.card}>
      <View style={styles.container}>
        <Text
          style={styles.summary}
          numberOfLines={2}
        >{`${customEvent.summary}`}</Text>

        {canEditCustomEvent ? (
          <PressableOpacity
            disabled={!onlineManager.isOnline()}
            onPress={() => {
              onEditCustomEvent(customEvent)
            }}
          >
            <Text style={styles.text}>Edit</Text>
          </PressableOpacity>
        ) : null}
      </View>
      <EventVisibilityText visibility={customEvent.visibleToRoles} />
      <ReadMoreView
        textStyle={styles.text}
        onPressReadMore={setModalVisible}
        description={customEvent.description}
      />
      <EventDescriptionModal
        visible={modalVisible}
        setModalVisible={setModalVisible}
        description={customEvent.description}
        title={customEvent.summary}
      />
    </Card>
  )
}

function EventVisibilityText({ visibility }: { visibility: Visibility }) {
  const styles = useStyles()
  switch (visibility.role) {
    case 'OBSERVER':
    case 'OBSERVER_MANAGER':
      return (
        <Text style={[styles.text, styles.prefixText]}>{`( ${t(
          visibility.titleKey
        )} ) `}</Text>
      )
    case 'ALL':
      return null
  }
}

const useStyles = makeStyles(theme => ({
  card: {
    marginTop: CUSTOM_EVENT_TOP_MARGIN,
    backgroundColor: '#ACCFD5',
    height: CUSTOM_EVENT_HEIGHT,
  },
  container: {
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  summary: {
    flex: 1,
    fontSize: 18,
    fontFamily: font.bold,
    color: '#353B4A',
    textAlignVertical: 'center',
  },
  text: {
    padding: 2,
    margin: 2,
    color: '#353B4A',
  },
  dividerText: {
    fontWeight: 'normal',
    marginBottom: 10,
    marginTop: 3,
    textAlignVertical: 'center',
  },
  seeMoreLessStyle: {
    color: theme.colors.primary,
  },
  prefixText: {
    fontStyle: 'italic',
  },
}))
